/**
 * 14 #9 — "Sunday digest to owners": every Sunday 07:00 GST each owner gets a bell notification with their blocked
 * drawings, oldest first ("23 in Ajmal Makan for 14 d · 15 in ATS Majlis SAMHA for 86 d"), and the same list by
 * email when Outgoing email is set up. The preview below is the digest as it would go out now — it always works.
 * Admins switch it on / off (default off) and say which account an owner name typed on drawings belongs to.
 * A closest-name guess ('mohanad' → Mohannad Atia) is only a preview: nothing goes to that account until an Admin
 * presses Confirm (stored as an Admin's choice), so a colleague's blocked list never reaches the wrong person.
 * Server: GET /api/shop-drawings/digest, PUT /api/shop-drawings/digest/settings, job shop_drawing_weekly_digest.
 * `readOnly` (BUG-002): an Admin whose Shop Drawings level is View gets the preview only — the switch and the owner
 * mapping are a Shop Drawings write the server refuses for that role.
 */
import { useCallback, useEffect, useState } from 'react';
import { BellRing, Loader2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotConfigured } from '@/components/ui/not-configured';
import { notify } from '@/components/ui/notify';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { Switch } from '@/components/ui/switch';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { sdRequest } from './api';
import { displayPerson } from './model';
import { AgeText, PersonAvatar } from './SdUi';

/** 'since 16 Sep'; an age counted from the Excel import / the day a drawing was added says so (14 #6, as the list). */
const sinceWords = (project) => {
  const day = formatDate(project.since, { year: 'auto' });
  if (project.since_source === 'import') return `since the ${day} import`;
  if (project.since_source === 'added') return `since added ${day}`;
  return `since ${day}`;
};

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const MATCH_TEXT = {
  mapped: 'chosen by an Admin',
  name: 'same name',
  email: 'same mailbox',
  first_name: 'same first name',
  similar: 'closest name',
  skipped: 'nobody (an Admin chose so)',
};

/** Loads the digest; `reload()` after a change. */
export function useDigest(enabled = true) {
  const [state, setState] = useState({ data: null, loading: false, error: '' });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const res = await sdRequest('get', '/digest');
      setState({ data: res.data, loading: false, error: '' });
    } catch (err) {
      setState((current) => ({ ...current, loading: false, error: getErrorMessage(err, 'Could not load the digest') }));
    }
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  return { ...state, reload: load, setData: (data) => setState({ data, loading: false, error: '' }) };
}

/** Toolbar button: "Sunday digest to owners" with its on / off state. */
export function DigestButton({ digest, onClick }) {
  const on = Boolean(digest.data?.settings?.enabled);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-ink-2"
      onClick={onClick}
      title="Every Sunday 07:00, each owner gets their blocked drawings, oldest first"
      data-testid="digest-button"
    >
      <BellRing aria-hidden="true" /> Sunday digest to owners
      <span
        className={cn('relative inline-block h-[16px] w-[28px] shrink-0 rounded-full transition-colors', on ? 'bg-[color:var(--status-green,#16a34a)]' : 'bg-[color:var(--line)]')}
        aria-hidden="true"
      >
        <span className={cn('absolute top-[2px] h-3 w-3 rounded-full bg-white shadow transition-[left]', on ? 'left-[14px]' : 'left-[2px]')} />
      </span>
      <span className="sr-only">{digest.data ? (on ? 'on' : 'off') : ''}</span>
    </Button>
  );
}

/** "Last run …" of the real job (the server never returns a dry run): what it did, in words. */
export function lastRunText(run) {
  if (!run) return '';
  const summary = run.summary || {};
  const parts = [`Last run ${formatDateTime(run.started_at)}`, run.status];
  if (summary.switched_off) parts.push('the digest was off, nothing was sent');
  else if (summary.notified !== undefined) parts.push(`${fmt(summary.notified)} notified, ${fmt(summary.emailed || 0)} emailed`);
  if (summary.unconfirmed) parts.push(`${fmt(summary.unconfirmed)} waiting for an Admin to confirm`);
  if (run.reason) parts.push(run.reason);
  return parts.join(' · ');
}

function OwnerRow({ entry, users, canManage, onMap, saving }) {
  const user = entry.user;
  // Older servers did not send `confirmed`: only an exact match counts then.
  const confirmed = entry.confirmed ?? (Boolean(user) && entry.match !== 'similar');
  return (
    <li className="rounded-xl border border-line bg-surface px-3 py-2.5" data-testid={`digest-owner-${entry.owner_key}`}>
      <div className="flex items-center gap-2.5">
        <PersonAvatar name={displayPerson(entry.owner)} size={26} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-ink">{displayPerson(entry.owner)} <span className="font-medium text-ink-3">· {fmt(entry.count)} blocked</span></p>
          <p className="truncate text-[11.5px] text-ink-3">
            {user && confirmed ? <>Goes to <b className="font-semibold text-ink-2">{user.name}</b>{user.email ? ` (${user.email})` : ''} · {MATCH_TEXT[entry.match] || 'matched'}</> : null}
            {user && !confirmed ? (
              <span className="font-semibold text-status-amber-ink" data-testid={`digest-unconfirmed-${entry.owner_key}`}>
                Closest name: {user.name} — not sent until an Admin confirms
              </span>
            ) : null}
            {!user ? (entry.match === 'skipped' ? 'Nobody gets this digest (an Admin chose so)' : <span className="font-semibold text-status-amber-ink">No account found — nobody gets this digest</span>) : null}
          </p>
        </div>
        {user && !confirmed && canManage ? (
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="shrink-0"
            onClick={() => onMap(entry.owner, user.id, `${displayPerson(entry.owner)}’s digest goes to ${user.name}`)}
            disabled={saving}
            aria-label={`Confirm: ${displayPerson(entry.owner)}’s digest goes to ${user.name}`}
            data-testid={`digest-confirm-${entry.owner_key}`}
          >
            Confirm
          </Button>
        ) : null}
        {entry.oldest_days !== null && entry.oldest_days !== undefined ? <span className="shrink-0 text-[12px]">oldest <AgeText days={entry.oldest_days} className="text-[12.5px] font-bold" /></span> : null}
      </div>
      <ul className="mt-2 space-y-0.5 pl-[36px] text-[12.5px] text-ink-2">
        {entry.projects.slice(0, 6).map((project) => (
          <li key={project.project} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate"><b className="font-semibold text-ink">{fmt(project.count)}</b> in {project.project}{project.project_no ? <span className="font-mono text-[11px] text-ink-3"> #{project.project_no}</span> : null}</span>
            <span className="shrink-0 text-ink-3">for <AgeText days={project.oldest_days} className="font-semibold" />{project.since ? <span className="hidden sm:inline"> · {sinceWords(project)}</span> : null}</span>
          </li>
        ))}
        {entry.projects.length > 6 ? <li className="text-ink-3">+ {fmt(entry.projects.length - 6)} more projects</li> : null}
      </ul>
      {canManage ? (
        <label className="mt-2 flex items-center gap-2 pl-[36px] text-[12px] text-ink-3">
          Send to
          <select
            value={entry.match === 'skipped' ? '__nobody__' : (confirmed ? user?.id || '' : '')}
            onChange={(event) => onMap(entry.owner, event.target.value === '__nobody__' ? '' : event.target.value)}
            disabled={saving}
            className="h-7 min-w-0 max-w-[260px] rounded-lg border border-line bg-surface pl-2 pr-7 text-[12px] font-semibold text-ink outline-none focus:ring-2 focus:ring-ring"
            aria-label={`Who gets ${displayPerson(entry.owner)}’s digest`}
            data-testid={`digest-map-${entry.owner_key}`}
          >
            {!confirmed && entry.match !== 'skipped' ? <option value="">Pick an account…</option> : null}
            {(users || []).map((item) => <option key={item.id} value={item.id}>{item.name}{item.email ? ` · ${item.email}` : ''}</option>)}
            <option value="__nobody__">Nobody</option>
          </select>
        </label>
      ) : null}
    </li>
  );
}

export function DigestPanel({ open, onOpenChange, digest, onShowOwner, readOnly = false }) {
  const [saving, setSaving] = useState(false);
  const data = digest.data;
  const canManage = Boolean(data?.can_manage) && !readOnly;
  const enabled = Boolean(data?.settings?.enabled);

  const save = async (patch, message) => {
    setSaving(true);
    try {
      const res = await sdRequest('put', '/digest/settings', patch);
      digest.setData(res.data);
      notify.success(message);
    } catch (err) {
      notify.error('Could not change the digest', { description: getErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const lastRun = data?.last_run;
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title="Sunday digest to owners"
      subtitle={`${data?.schedule_label || 'Every Sunday 07:00 GST'} · each owner gets their blocked drawings, oldest first`}
      icon={BellRing}
      iconTone="navy"
      hideFooter
      testId="digest-panel"
    >
      <SidePanelSection title="Weekly digest">
        <div className="flex items-start gap-3 rounded-xl border border-line bg-surface px-3.5 py-3">
          {readOnly ? null : (
            <Switch
              checked={enabled}
              onCheckedChange={(value) => save({ enabled: value }, value ? 'Sunday digest switched on' : 'Sunday digest switched off')}
              disabled={!canManage || saving || !data}
              aria-label="Send the Sunday digest"
              data-testid="digest-switch"
            />
          )}
          <div className="min-w-0 text-[12.5px] text-ink-2">
            <p className="text-[13px] font-bold text-ink">{enabled ? 'On — goes out every Sunday 07:00' : 'Off — nothing is sent'}</p>
            <p className="mt-0.5">
              {canManage ? 'A bell notification for each owner; email too when Outgoing email is set up.' : readOnly ? 'Shop Drawings is view only for your role.' : 'An Admin can switch it on or off.'}
              {data?.settings?.updated_by ? ` Changed by ${data.settings.updated_by}${data.settings.updated_at ? ` on ${formatDate(data.settings.updated_at)}` : ''}.` : ''}
            </p>
            {canManage && lastRun ? (
              <p className="mt-1 text-ink-3" data-testid="digest-last-run">{lastRunText(lastRun)}</p>
            ) : null}
          </div>
          {saving ? <Loader2 className="ml-auto h-4 w-4 animate-spin text-ink-3" aria-hidden="true" /> : null}
        </div>
        {data && !data.email_enabled ? (
          <NotConfigured
            feature="email_outbound"
            layout="inline"
            title="Bell notifications only"
            text="Email copies of the digest go out once Outgoing email is set up."
            className="mt-2"
            testId="digest-email-off"
          />
        ) : null}
      </SidePanelSection>

      <SidePanelSection
        title={data ? `This week · ${fmt(data.total_blocked)} blocked` : 'This week'}
        description="The digest as it would go out now."
        actions={(
          <Button type="button" variant="ghost" size="xs" onClick={digest.reload} disabled={digest.loading} aria-label="Refresh the preview">
            <RotateCw className={cn(digest.loading && 'animate-spin')} aria-hidden="true" /> Refresh
          </Button>
        )}
      >
        {digest.error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{digest.error}</p> : null}
        {!data && digest.loading ? <p className="text-[12.5px] text-ink-3">Loading…</p> : null}
        {data ? (
          <ul className="space-y-2" data-testid="digest-owners">
            {data.owners.map((entry) => (
              <OwnerRow key={entry.owner_key} entry={entry} users={data.users} canManage={canManage} saving={saving} onMap={(owner, userId, message) => save({ owner_map: { [owner]: userId } }, message || 'Digest recipient saved')} />
            ))}
            {!data.owners.length ? <li className="text-[12.5px] text-ink-3">No blocked drawing has an owner yet.</li> : null}
            {data.unowned ? (
              <li className="flex items-center gap-2 rounded-xl border border-dashed border-[color:var(--line-dashed)] px-3 py-2.5 text-[12.5px] text-ink-2" data-testid="digest-unowned">
                <span className="min-w-0 flex-1"><b className="font-semibold text-ink">{fmt(data.unowned.count)}</b> blocked {data.unowned.count === 1 ? 'drawing has' : 'drawings have'} no owner — they are in nobody’s digest.</span>
                <Button type="button" size="xs" variant="outline" onClick={() => onShowOwner('__none__')}>Show them</Button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </SidePanelSection>
    </SidePanel>
  );
}
